<template>
  <div class="w-48">
    <div class="mb-2 text-xs font-semibold text-gray-400 uppercase">
      {{ $t("BORROWER_PLATFORM.TILES_LAYOUT") }}
    </div>
    <div
      class="flex items-center rounded-5 border border-gray-200 bg-white px-6 py-1"
    >
      <lf-dropdown
        v-model="layout"
        :options="layoutOptionsAsArray"
        :icons="layoutIcons"
        :disabled="disabled"
        data-cy="tiles-layout-dropdown"
        :full-width="false"
        :search-enabled="false"
        name="layout"
        class="flex-none"
        menu-width="w-10"
        @change="handleLayoutChange"
      >
        <template #toggle-button="{ toggle, menuOpen }">
          <div class="flex items-center space-x-1" @click="toggle">
            <icon-base :icon="layoutIcons[layout].icon" />
            <icon-chevron-down
              class="transform transition-transform"
              :class="{ 'rotate-180': !menuOpen }"
            />
          </div>
        </template>
      </lf-dropdown>
      <div class="mx-6 h-8 w-px bg-gray-200" />
      <div class="flex items-center gap-1">
        <button
          :disabled="blockCount <= minBlocks || disabled"
          class="text-gray-500 hover:text-blue-600 focus:outline-none"
          :class="{ 'cursor-not-allowed opacity-50': disabled }"
          data-cy="tiles-layout-minus-btn"
          @click="decrement"
        >
          <span class="text-2xl">-</span>
        </button>
        <span
          class="w-6 text-center text-lg font-medium text-gray-500"
          data-cy="tiles-layout-block-count"
        >
          {{ blockCount }}
        </span>
        <button
          :disabled="blockCount >= maxBlocks || disabled"
          class="-mt-0-5 text-gray-500 hover:text-blue-600 focus:outline-none"
          :class="{ 'cursor-not-allowed opacity-50': disabled }"
          data-cy="tiles-layout-plus-btn"
          @click="increment"
        >
          <span class="text-2xl">+</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import { BlockLayout } from "@/models/borrowerPlatformTemplates";

import IconOneColumn from "@/components/icons/borrowerPlatform/borrowerTabs/IconOneColumn.vue";
import IconTwoColumns from "@/components/icons/borrowerPlatform/borrowerTabs/IconTwoColumns.vue";
import IconChevronDown from "@/components/icons/deals/IconChevronDown.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

interface Props {
  modelValue: {
    layout: BlockLayout;
    blockCount: number;
  };
  minBlocks?: number;
  maxBlocks?: number;
  disabled?: boolean;
}

const {
  modelValue,
  minBlocks = 1,
  maxBlocks = 10,
  disabled = false,
} = defineProps<Props>();
const emit = defineEmits<{
  "update:modelValue": [value: { layout: BlockLayout; blockCount: number }];
}>();

const layout = ref(modelValue.layout);
const blockCount = ref(modelValue.blockCount);

const layoutOptions = [
  { value: BlockLayout.ONE_COLUMN, label: "", icon: IconOneColumn },
  { value: BlockLayout.TWO_COLUMNS, label: "", icon: IconTwoColumns },
];

const layoutOptionsAsArray = Object.fromEntries(
  layoutOptions.map((option) => [option.value, option.label])
);

const layoutIcons = Object.fromEntries(
  layoutOptions.map((option) => [option.value, { icon: option.icon }])
);

const handleLayoutChange = (value: string | number) => {
  layout.value = value as BlockLayout;
  emitUpdate();
};

const increment = () => {
  if (blockCount.value >= maxBlocks) {
    return;
  }
  blockCount.value++;
  emitUpdate();
};

const decrement = () => {
  if (blockCount.value <= minBlocks) {
    return;
  }
  blockCount.value--;
  emitUpdate();
};

const emitUpdate = () => {
  emit("update:modelValue", {
    layout: layout.value,
    blockCount: blockCount.value,
  });
};

watch(
  () => modelValue,
  (val) => {
    layout.value = val.layout;
    blockCount.value = val.blockCount;
  },
  { immediate: true }
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";
:deep(.dropdown-option) {
  text-align: center !important;
  justify-content: center !important;
  padding-right: 0 !important;
}
:deep(.dropdown-option .capitalize) {
  display: none !important;
}
</style>
